import {FC, useCallback, useEffect, useState} from 'react'
import FormTabHeader, {TabOption} from '../../../../shared/components/FormTabHeader'
import {initialStopSell, IStopSellModel, validationStopSell} from './StopSellModel'
import {createStopSell, deleteStopSell, loadStopSell, updateStopSell} from './Api'
import {showErrorToast, showSuccessToast} from '../../../../shared/components/ToastUtils'
import {Form, Formik, FormikProps} from 'formik'
import SelectInput from '../../../../shared/components/SelectInput'
import TextInput from '../../../../shared/components/TextInput'
import {StopSellTable} from './StopSellTable'
import {
  branches,
  reservationSources,
  vehicleGroups,
} from '../../../../shared/models/SharedApiLookups'

const StopSellPage: FC = () => {
  const [tab, setTab] = useState<string>('form')
  const [loading, setLoading] = useState(false)
  const [selectedId, setSelectedId] = useState<string | null | undefined>(null)
  const [controlSaveButton, setControlSaveButton] = useState(true)
  const [showModal, setShowModal] = useState(false)
  const [error, setError] = useState<string | null>(null)
  const [stopSell, setStopSell] = useState<IStopSellModel[]>([])
  const [totalPages, setTotalPages] = useState(0)
  const [currentPage, setCurrentPage] = useState(0)

  const tabOptions: TabOption<'form'>[] = [
    {key: 'form', title: 'Broker Yasakaları (Stop Sell)', icon: 'bi bi-geo-alt'},
  ]

  const [reservationsourceOptions, setReservationSourceOptions] = useState<
    {value: string; label: string}[]
  >([{value: 'Rezervasyon', label: 'Rezervasyon 1'}])
  const [vehicleGroupOptions, setVehicleGroupOptions] = useState<{value: string; label: string}[]>([
    {value: 'Araç Grubu', label: 'Araç Grubu 1'},
  ])
  const [branchOptions, setBranchOptions] = useState<{value: string; label: string}[]>([
    {value: 'Şube', label: 'Şube 1'},
  ])

  // useEffect(() => {
  //   Promise.all([reservationSources(), vehicleGroups(), branches()]).then(([rs, vg, brn]) => {
  //     setReservationSourceOptions(rs)
  //     setVehicleGroupOptions(vg)
  //     setBranchOptions(brn)
  //   })
  // }, [])

  const fetchStopSell = useCallback(
    async (page: number = 0, sort?: string, order?: 'asc' | 'desc') => {
      setLoading(true)
      setError(null)
      try {
        const response = await loadStopSell(page, 5, sort, order)
        if (response.data.hasError) {
          throw new Error(response.data.message || 'Failed to fetch stop sell')
        }
        setStopSell(response.data.payload.content)
        setTotalPages(response.data.payload.totalPages)
      } catch (err) {
        setError(err instanceof Error ? err.message : 'Unknown error occurred')
      } finally {
        setLoading(false)
      }
    },
    []
  )

  useEffect(() => {
    fetchStopSell(0)
  }, [fetchStopSell])

  const handleSave = async (values: IStopSellModel, resetForm: () => void) => {
    setLoading(true)
    try {
      if (selectedId) {
        await updateStopSell({...values, id: selectedId})
        showSuccessToast('Stop Sell başarıyla güncellendi')
      } else {
        await createStopSell(values)
        showSuccessToast('Stop Sell başarıyla oluşturuldu')
      }
      await fetchStopSell(0)
      resetForm()
      setSelectedId(null)
      setTab('form')
    } catch (error) {
      showErrorToast('Bir hata oluştu')
    } finally {
      setLoading(false)
    }
  }

  const handleEdit = useCallback(
    (stopSell: IStopSellModel, formik: FormikProps<IStopSellModel>) => {
      formik.setValues(stopSell)
      setSelectedId(stopSell.id)
      setTab('form')
    },
    []
  )

  const handleDelete = async (stopSell: IStopSellModel) => {
    if (stopSell.id) {
      setLoading(true)
      try {
        await deleteStopSell(stopSell.id)
        showSuccessToast('Stop Sell başarıyla silindi')
        await fetchStopSell(currentPage)
      } catch (err) {
        console.error(err)
        showErrorToast('Stop Sell silinirken bir hata oluştu')
      } finally {
        setLoading(false)
        setShowModal(false)
        setSelectedId(null)
      }
    }
  }

  const handlePageChange = useCallback(
    (newPage: number) => {
      if (newPage >= 0 && newPage < totalPages) {
        setCurrentPage(newPage)
        fetchStopSell(newPage)
      }
    },
    [totalPages, fetchStopSell]
  )

  return (
    <>
      <Formik
        initialValues={initialStopSell}
        validationSchema={validationStopSell}
        enableReinitialize={true}
        onSubmit={(values, {resetForm}) => handleSave(values, resetForm)}
      >
        {(formik) => {
          const handleReset = () => {
            formik.resetForm()
            setSelectedId(null)
          }

          return (
            <>
              <FormTabHeader
                formId='stopSellForm'
                formikSubmit={formik.handleSubmit}
                formikReset={handleReset}
                loading={loading}
                tab={tab}
                setTab={setTab}
                setSelectedId={setSelectedId}
                tabs={tabOptions}
                onExportExcel={() => console.log('Excel export')}
              />
              {tab === 'form' && (
                <div className='card mb-5'>
                  <Form id='stopSellForm' className='form card-body'>
                    <div className='row mb-6'>
                      <SelectInput
                        name='reservationSource'
                        label='Rezervasyon Kaynağı'
                        wrapperClassName='col-md-6 mb-2'
                        options={reservationsourceOptions}
                      />
                      <TextInput
                        name='startDate'
                        type='date'
                        label='Başlangıç Tarihi'
                        wrapperClassName='col-6 mb-2'
                      />
                      <SelectInput
                        name='vehicleGroups'
                        label='Araç Grupları'
                        wrapperClassName='col-md-6 mb-2'
                        options={vehicleGroupOptions}
                      />
                      <TextInput
                        name='endDate'
                        type='date'
                        label='Bitiş Tarihi'
                        wrapperClassName='col-6 mb-2'
                      />
                      <SelectInput
                        name='branches'
                        label='Şubeler'
                        wrapperClassName='col-md-6 mb-2'
                        options={branchOptions}
                      />
                      <TextInput
                        name='minimumDays'
                        type='number'
                        label='Minimum Gün'
                        wrapperClassName='col-6 mb-2'
                      />
                    </div>
                  </Form>
                </div>
              )}
              <div className='card'>
                <div className='card-body pt-0'>
                  <StopSellTable
                    data={stopSell}
                    currentPage={currentPage}
                    totalPages={totalPages}
                    onPageChange={handlePageChange}
                    onEdit={(stopSell) => handleEdit(stopSell, formik)}
                    onDelete={handleDelete}
                    loading={loading}
                  />
                </div>
              </div>
            </>
          )
        }}
      </Formik>
    </>
  )
}

export {StopSellPage}
